<script type="text/javascript">
	import moment from 'moment';
	import Chart from "chart.js";
	import { onMount, afterUpdate, tick, onDestroy } from 'svelte';
	import { get, writable } from 'svelte/store';

	import Vessel from './Vessel';
	import VesselIcon from './Enums/VesselIcon';
	import VesselProperty from './Enums/VesselProperty';

	import User from '../Branding/User';
	import Feature from '../Branding/Feature';

	export let vessel;							// model (Vessel | Aircraft)
	export let property;						// property (VesselProperty)
	export let propertyOptions = [];			// property (VesselProperty)
	export let height;							// height in pixels (numeric)

	let inProperty = writable(property);

	let yAxisLabel = "";

	let formatPropertyName = (property) => {
		return property.substr(0,1).toUpperCase() + property.substr(1);
	}
	let formatPropertySet = (set) => {
		if (typeof set === "string") {
			return formatPropertyName(set);
		}
		return set.map((property)=>{ return formatPropertyName(property); }).join("/");
	};

	let doProperties = [];
	let titleDisplayString = "";

	let timeSliderValue = writable(0);
	let timeSliderValueMax = writable(0);

	$: {
		$timeSliderValue;
		$timeSliderValueMax;
		if ($vessel) {
			timeSliderValue = $vessel.group.timeSliderData.value;
			timeSliderValueMax = $vessel.group.timeSliderData.max;
		}
	}
	$: {
		$inProperty;
		updateChartAxes();
		updateChartData();
	}

	let canvas = null;
	let chart = null;

	let dataPointsBeforeTime = null;
	let dataPoints = null;

	onMount(()=>{
		Chart.defaults.global.defaultFontFamily = "'Open Sans', 'Arial', sans-serif";
		// Chart.defaults.global.animation.duration = 0;
		updateChart();
	});
	onDestroy(()=>{
		if (chart !== null) {
			chart.destroy();
			chart = null;
		}
	});

	let updateChartAxes = () => {
		if ($inProperty === VesselProperty.SPEED) {
			yAxisLabel = "Speed (Knots)";
		}
		else if ($inProperty === VesselProperty.COURSE || $inProperty === VesselProperty.HEADING) {
			yAxisLabel = "Degrees";
		}
		else {
			yAxisLabel = $inProperty;
		}

		if ($inProperty === VesselProperty.SPEED) {
			doProperties = [VesselProperty.SPEED];
		}
		else if ($inProperty === VesselProperty.COURSE || $inProperty === VesselProperty.HEADING) {
			doProperties = [VesselProperty.COURSE, VesselProperty.HEADING];
			if (typeof $vessel.historicPath.stops.getPropertyForIndex(0, VesselProperty.HEADING) === "undefined") {
				doProperties = [VesselProperty.COURSE];
			}
		}
		if (chart !== null) {
			chart.options.scales.yAxes[0].scaleLabel.labelString = yAxisLabel;
			titleDisplayString = formatPropertySet(doProperties);

			// TODO: downsample dataset?
			chart.data.datasets = [];

			let createDataset = (label, color, xaxisid = "x", yaxisid = "y") => {
				return {
					label: label,
					borderColor: color,
					backgroundColor: color,
					borderWidth: 0.75,
					rotation: 0,
					lineTension: 0,
					steppedLine: false,
					fill: false,
					xAxisID: "x",
					yAxisID: "y",
					data: []
				}
			}

			if ($inProperty === VesselProperty.SPEED) {
				chart.options.scales.yAxes[0].ticks.suggestedMin = 0;
				chart.options.scales.yAxes[0].ticks.suggestedMax = 18;
				chart.options.scales.yAxes[0].ticks.stepSize = 1;

				let vesselColor = VesselIcon.helpers.toColor($vessel.iconImage);
				if (vesselColor === "white") {
					vesselColor = "black";
				}
				chart.data.datasets.push(createDataset("", vesselColor));
				chart.data.datasets.push(createDataset("Speed", "grey"));
			}
			else if ($inProperty === VesselProperty.COURSE || $inProperty === VesselProperty.HEADING) {

				chart.options.scales.yAxes[0].ticks.suggestedMin = 0;
				chart.options.scales.yAxes[0].ticks.suggestedMax = 360;
				chart.options.scales.yAxes[0].ticks.stepSize = 30;

				chart.data.datasets.push(createDataset("", "red"));
				chart.data.datasets.push(createDataset("", "blue"));
				chart.data.datasets.push(createDataset("Course", "grey"));
				chart.data.datasets.push(createDataset("Heading", "grey"));
			}

			chart.update();
		}
	}
	let updateChartData = () => {

		dataPointsBeforeTime = {};
		dataPoints = {};
		for(let j = 0; j < doProperties.length; j++) {
			dataPointsBeforeTime[doProperties[j]] = [];
			dataPoints[doProperties[j]] = [];
		}

		for(let i = 0; i < $vessel.historicPath.stops.length; i++) {
			const time = moment($vessel.historicPath.stops.getTimestampForIndex(i));
			const sliderTime = moment(get($vessel.group.timeSliderData.value));

			for(let j = 0; j < doProperties.length; j++) {
				const point = {
					x: time.toISOString(),
					y: $vessel.historicPath.stops.getPropertyForIndex(i, doProperties[j])
				};
				dataPoints[doProperties[j]].push(point);
				if (time <= sliderTime) {
					dataPointsBeforeTime[doProperties[j]].push(point);
				}
			}
		}
		if (chart !== null) {

			if ($inProperty === VesselProperty.SPEED) {

				chart.data.datasets[0].data = dataPointsBeforeTime[VesselProperty.SPEED];
				chart.data.datasets[1].data = dataPoints[VesselProperty.SPEED];

			}
			else if ($inProperty === VesselProperty.COURSE || $inProperty === VesselProperty.HEADING) {
				chart.data.datasets[0].data = dataPointsBeforeTime[VesselProperty.COURSE];
				chart.data.datasets[1].data = dataPointsBeforeTime[VesselProperty.HEADING];
				chart.data.datasets[2].data = dataPoints[VesselProperty.COURSE];
				chart.data.datasets[3].data = dataPoints[VesselProperty.HEADING];
			}
			chart.update();
		}
	};

	$: {
		$timeSliderValue;
		$timeSliderValueMax;
		if (canvas !== null) {
			updateChartData();
		}
	}

	let updateChart = ()=> {

		if (canvas !== null) {

			let config = {
				type: 'line',
				data: {
					datasets: []
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					title: {
						display: false
					},
					legend: {
						display: false,
						onClick: () => {}
					},
					tooltips: {
						enabled: true,
						filter: (item) => {
							if (item.datasetIndex < doProperties.length) { return false; }
							return true;
						},
						callbacks: {
							label: (item, data) => {
								let useIndex = item.datasetIndex % doProperties.length;
								return formatPropertyName(doProperties[useIndex]) + ": " +
										Vessel.formatProperty(item.value, doProperties[useIndex]);
							},
							footer: () => {
								if (User.hasFeature(Feature.TIME_SLIDER.VIEW)) {
									return "Click to change timeline.";
								}
								return "";
							}
						}
					},
					onClick: (event, elements) => {
						if (!User.hasFeature(Feature.TIME_SLIDER.VIEW)) {
							return;
						}
						if (elements.length > 0) {
							let index = elements[0]._index;
							let datasetIndex = elements[0]._datasetIndex;

							const timestamp = $vessel.historicPath.stops.getTimestampForIndex(index);
							$vessel.group.timeSliderData.value.set(timestamp);
						}
					},
					scales: {
						xAxes: [
							{
								id: "x",
								type: "time",
								distribution: "linear",
								ticks: {
									source: 'auto'
								},
								scaleLabel: {
									display: true,
									position: "bottom",
									labelString: "Time"
								},
								time: {
									minUnit: "day",
									tooltipFormat: "MMMM Do YYYY, hh:mm:ssa [(UTC)]"
									// BUG: Using these crashes the browser with current  chart.js/moment versions.
									// unit: 'hourly',
									// displayFormats: {
									//  	hourly: "MMM D - HH:mm"
									// }
								},
								afterBuildTicks: function(scale, ticks) {
									return ticks;
								}
							}
						],
						yAxes: [
							{
								id: "y",
								scaleLabel: {
									display: true,
									position: "left",
									labelString: yAxisLabel
								},
								ticks: {
									//beginAtZero: false,
									stepSize: 1
								}
							}
						]
					}
				}
			};

			chart = new Chart(canvas.getContext("2d"), config);
			chart.update();
			updateChartAxes();
			updateChartData();
		}

	};

</script>

{#if propertyOptions.length > 0}
	<select bind:value={$inProperty} class="default" style="margin-bottom:8px; margin-top:8px; font-weight:bold; text-align-last:center; text-align:center;">
		{#each propertyOptions as option}
			{#if typeof option === "string"}
				<option value={option}>{formatPropertySet(option)}</option>
			{:else}
				<option value={option[0]}>{formatPropertySet(option)}</option>
			{/if}
		{/each}
	</select>
{:else}
	<p style="text-align:center;"><b>{ titleDisplayString }</b></p>
{/if}
<div class="infocard-chart-container" style={"position:relative; width:100%; min-height:" + height + "px; height:" + height + "px;"}>
	<canvas bind:this={canvas}></canvas>
</div>
